import { Button } from '@/components/ui/button'
import { Camera, SkipForward, RotateCcw, Check, Loader2 } from 'lucide-react'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { CameraUploadButton } from '@/components/installFlow/CameraUploadButton'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useState, useEffect } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { useUploadNameplateScan } from '@/installFlow/nameplateScan/api'

/**
 * ScanStep - Nameplate photo upload step
 *
 * Positioned after IntroStep in the install flow.
 * Allows users to upload a photo of the equipment nameplate for auto-filling details.
 * Users can skip this step if they prefer to enter details manually.
 *
 * Flow:
 * 1. User takes photo
 * 2. Local image preview is shown (not uploaded yet)
 * 3. User can retake or proceed with "Looks good"
 * 4. On "Looks good", image is uploaded and user proceeds
 */
export function ScanStep() {
  const { goToStep } = useInstallFlowNavigation()
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const uploadMutation = useUploadNameplateScan()

  // Create preview URL when file is selected
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(selectedFile)
    setPreviewUrl(objectUrl)

    // Cleanup object URL when component unmounts or file changes
    return () => URL.revokeObjectURL(objectUrl)
  }, [selectedFile])

  const handleFileSelected = (file: File) => {
    setSelectedFile(file)
    setUploadError(null)
  }

  const handleSkip = () => {
    // Navigate to location step without scanId
    goToStep('location')
  }

  const handleRetake = () => {
    // Clear selected file to allow retake
    setSelectedFile(null)
    setUploadError(null)
  }

  const handleProceed = async () => {
    // Upload the file and navigate to location step with scanId
    if (!selectedFile) return

    try {
      const result = await uploadMutation.mutateAsync(selectedFile)
      goToStep('location', { scanId: result.id })
    } catch (error) {
      console.error('Failed to upload nameplate scan:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to upload image'
      setUploadError(errorMessage)
    }
  }

  // Show preview state when file is selected
  const showPreview = !!previewUrl

  return (
    <StepFrame
      main={
        <div className="flex flex-col gap-6 items-center justify-center">
          {!showPreview ? (
            <>
              {/* Header */}
              <div className="flex flex-col space-y-2 items-center justify-center text-center">
                <div className="w-fit aspect-square rounded-lg bg-thalo-blueLight p-4 flex justify-center items-center">
                  <Camera className="text-white" size={48} />
                </div>
                <h1 className="text-2xl font-semibold">Scan Equipment Nameplate</h1>
                <div className="text-muted-foreground space-y-2">
                  <p>
                    Take a photo of the equipment nameplate to automatically fill in details like
                    manufacturer, model, and serial number.
                  </p>
                  <p className="text-sm">
                    Don't worry - you can review and edit everything before saving.
                  </p>
                </div>
              </div>

              {/* Upload Button */}
              <div className="w-full max-w-md">
                <CameraUploadButton
                  onFileSelected={handleFileSelected}
                />
              </div>

              {/* Error Alert */}
              {uploadError && (
                <Alert variant="destructive" className="max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}

              {/* Helper Text */}
              <div className="text-sm text-muted-foreground text-center max-w-md space-y-2">
                <p>
                  <strong>Tips for best results:</strong>
                </p>
                <ul className="text-left list-disc list-inside space-y-1">
                  <li>Ensure good lighting</li>
                  <li>Get close enough to read the text clearly</li>
                  <li>Keep the camera steady</li>
                  <li>Include the entire nameplate in the photo</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Image Preview */}
              <div className="flex flex-col space-y-4 items-center justify-center text-center w-full max-w-2xl">
                <h1 className="text-2xl font-semibold">Review Your Photo</h1>
                <p className="text-muted-foreground">
                  Make sure the nameplate text is clear and readable.
                </p>
                <div className="w-full rounded-lg overflow-hidden border-2 border-gray-200 shadow-lg">
                  <img
                    src={previewUrl}
                    alt="Nameplate scan preview"
                    className="w-full h-auto"
                  />
                </div>

                {/* Error Alert in preview */}
                {uploadError && (
                  <Alert variant="destructive" className="max-w-md">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{uploadError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}
        </div>
      }
      footer={
        <StepFooter
          actions={
            !showPreview
              ? [
                  <Button
                    key="skip"
                    onClick={handleSkip}
                    variant="outline"
                    size="lg"
                    className="w-full"
                  >
                    <SkipForward className="h-4 w-4 mr-2" />
                    Skip - Enter Details Manually
                  </Button>,
                ]
              : [
                  <Button
                    key="retake"
                    onClick={handleRetake}
                    variant="outline"
                    size="lg"
                    className="w-full"
                    disabled={uploadMutation.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retake Photo
                  </Button>,
                  <Button
                    key="proceed"
                    onClick={handleProceed}
                    variant="thalo"
                    size="lg"
                    className="w-full"
                    disabled={uploadMutation.isPending}
                  >
                    {uploadMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Looks Good
                      </>
                    )}
                  </Button>,
                ]
          }
        />
      }
    />
  )
}